import { isPlatformBrowser } from '@angular/common';
import { Directive, ElementRef, inject, input, NgZone, OnChanges, OnDestroy, OnInit, output, PLATFORM_ID, SimpleChanges } from '@angular/core';

@Directive({ selector: '[baasClickOutside]', standalone: true })
export class BaasClickOutsideBehavior implements OnInit, OnChanges, OnDestroy {
    readonly #el = inject(ElementRef);
    readonly #ngZone = inject(NgZone);
    readonly #platformId = inject(PLATFORM_ID);

    readonly clickOutsideEnabled = input(true);
    readonly attachOutsideOnClick = input(false);
    readonly delayClickOutsideInit = input(false);
    readonly emitOnBlur = input(false);
    readonly exclude = input('');
    readonly excludeBeforeClick = input(false);
    readonly clickOutsideEvents = input('');
    readonly clickOutside = output<Event>();
    #nodesExcluded: Array<HTMLElement> = [];
    #events: Array<string> = ['click'];

    constructor() {
        this.initOnClickBody = this.initOnClickBody.bind(this);
        this.onClickBody = this.onClickBody.bind(this);
        this.onWindowBlur = this.onWindowBlur.bind(this);
    }

    ngOnInit() {
        if (!isPlatformBrowser(this.#platformId)) return;

        this.#init();
    }

    ngOnDestroy() {
        if (!isPlatformBrowser(this.#platformId)) {
            return;
        }

        this.#removeClickOutsideListener();
        this.#removeAttachOutsideOnClickListener();
        this.#removeWindowBlurListener();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!isPlatformBrowser(this.#platformId)) {
            return;
        }

        if (changes['attachOutsideOnClick'] || changes['exclude'] || changes['emitOnBlur']) {
            this.#init();
        }
    }

    // NOTE apparently we are assigning this method which is not allowed with #
    private initOnClickBody() {
        if (this.delayClickOutsideInit()) {
            setTimeout(this.#initClickOutsideListener.bind(this));
        } else {
            this.#initClickOutsideListener();
        }
    }

    private onClickBody(ev: Event) {
        if (!this.clickOutsideEnabled()) {
            return;
        }

        if (this.excludeBeforeClick()) {
            this.#excludeCheck();
        }

        if (!this.#el.nativeElement.contains(ev.target) && !this.#shouldExclude(ev.target)) {
            this.#emit(ev);

            if (this.attachOutsideOnClick()) {
                this.#removeClickOutsideListener();
            }
        }
    }

    /**
     * Resolves problem with outside click on iframe
     * @see https://github.com/arkon/ng-click-outside/issues/32
     */
    private onWindowBlur(ev: Event) {
        setTimeout(() => {
            if (!document.hidden) {
                this.#emit(ev);
            }
        });
    }

    #init() {
        const clickOutsideEvents = this.clickOutsideEvents();
        if (clickOutsideEvents !== '') {
            this.#events = clickOutsideEvents.split(',').map((e) => e.trim());
        }

        this.#excludeCheck();

        if (this.attachOutsideOnClick()) {
            this.#initAttachOutsideOnClickListener();
        } else {
            this.initOnClickBody();
        }

        if (this.emitOnBlur()) {
            this.#initWindowBlurListener();
        }
    }

    #excludeCheck() {
        const exclude = this.exclude();
        if (exclude) {
            try {
                const nodes = Array.from(document.querySelectorAll(exclude)) as Array<HTMLElement>;
                if (nodes) {
                    this.#nodesExcluded = nodes;
                }
            } catch (err) {
                console.error('[ng-click-outside] Check your exclude selector syntax.', err);
            }
        }
    }

    #emit(ev: Event) {
        if (!this.clickOutsideEnabled()) {
            return;
        }

        this.#ngZone.run(() => this.clickOutside.emit(ev));
    }

    #shouldExclude(target): boolean {
        for (const excludedNode of this.#nodesExcluded) {
            if (excludedNode.contains(target)) {
                return true;
            }
        }

        return false;
    }

    #initClickOutsideListener() {
        this.#ngZone.runOutsideAngular(() => {
            this.#events.forEach((e) => document.body.addEventListener(e, this.onClickBody));
        });
    }

    #removeClickOutsideListener() {
        this.#ngZone.runOutsideAngular(() => {
            this.#events.forEach((e) => document.body.removeEventListener(e, this.onClickBody));
        });
    }

    #initAttachOutsideOnClickListener() {
        this.#ngZone.runOutsideAngular(() => {
            this.#events.forEach((e) => this.#el.nativeElement.addEventListener(e, this.initOnClickBody));
        });
    }

    #removeAttachOutsideOnClickListener() {
        this.#ngZone.runOutsideAngular(() => {
            this.#events.forEach((e) => this.#el.nativeElement.removeEventListener(e, this.initOnClickBody));
        });
    }

    #initWindowBlurListener() {
        this.#ngZone.runOutsideAngular(() => {
            window.addEventListener('blur', this.onWindowBlur);
        });
    }

    #removeWindowBlurListener() {
        this.#ngZone.runOutsideAngular(() => {
            window.removeEventListener('blur', this.onWindowBlur);
        });
    }
}
